import { Injectable } from '@angular/core';
import { Observable, Subscriber, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { WeatherResult, Location } from '../models/types';

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  
  stations: string[] = [];
  stationIdx = 0;
  
  subscriber = new Subscriber();
  observable = new Observable<WeatherResult[]>(subscriber => {
    this.subscriber = subscriber;
  });

  constructor(
    private http: HttpClient
  ) { }
  
  private formatDate(date: Date): string {
    return date.toISOString().replace(/.\d+Z$/, 'Z');
  }
  
  fetch24HourAverages(latitude: number, longitude: number, startDate: Date, endDate: Date, numDays: number): Observable<WeatherResult[]> {
    this.stations = [];
    this.stationIdx = 0;
    const url = this.buildStationUrl(latitude, longitude);
    console.log(url);
    this.http.get<{[key: string]: any}>(url).subscribe(results => {
      this.stations = this.extractStations(results, 10);
      if (this.stations.length == 0) {
        this.subscriber.error('No stations');
      }
      this.fetchObservationsForStation(this.stations[this.stationIdx], startDate, endDate, numDays);
    }, error => {
      this.subscriber.error('No stations');
    });
    
    return this.observable;
  }
  
  private buildStationUrl(latitude: number, longitude: number): string {
    return `https://api.weather.gov/points/${latitude.toFixed(4)},${longitude.toFixed(4)}/stations`;
  }
  
  private extractStations(results: {[key: string]: any}, maxStations: number): string[] {
    let stations = [];
    for (const feature of results['features']) {
      const properties = feature['properties'];
      stations.push(properties['stationIdentifier']);
      
      if (stations.length >= maxStations) {
        break;
      }
    }
    return stations;
  }
  
  private fetchObservationsForStation(station: string, startDate: Date, endDate: Date, numDays: number) {
    const url = this.buildObsUrl(station, startDate, endDate);
    console.log(url);
    this.http.get<{[key: string]: any}>(url).subscribe(results => {
      const weatherResults = this.extractObservations(results, false, endDate, numDays);
      if (weatherResults.length > 0) {
        this.subscriber.next(weatherResults);
      } else {
        this.stationIdx += 1;
        if (this.stationIdx < this.stations.length) {
          this.fetchObservationsForStation(this.stations[this.stationIdx], startDate, endDate, numDays);
        } else {
          this.subscriber.error();
        }
      }
    });
  }
  
  private buildObsUrl(station: string, startDate: Date, endDate: Date): string {
    return `https://api.weather.gov/stations/${station}/observations?start=${this.formatDate(startDate)}&end=${this.formatDate(endDate)}`;
  }
  
  private extractObservations(results: {[key: string]: any}, returnHourly: boolean, endDate: Date, numDays: number): WeatherResult[] {
    let numObs: { [key: string]: number } = {};
    let summedTemperature: { [key: string]: number } = {};
    let summedWindSpeed: { [key: string]: number } = {};
    let summedHumidity: { [key: string]: number } = {};
    let latitude = -999;
    let longitude = -999;
    let station = '';
    
    let hourlyData: { [key: string]: [number, number, number, number, number] } = {};
    for (const feature of results['features']) {
      const geometry = feature['geometry'];
      const coordinates = geometry['coordinates'];
      
      const properties = feature['properties'];
      const timestamp = new Date(properties['timestamp']);
      const dayNum = Math.floor((endDate.getTime() - timestamp.getTime())/(1000*60*60*24));
      if (dayNum > (numDays - 1) || dayNum < 0) continue;
      timestamp.setMinutes(0, 0, 0);
      const hour = timestamp.toISOString();
      
      const temperatureDict = properties['temperature'];
      const temperature = temperatureDict['value'];
      if (temperature === null || temperatureDict['unitCode'] != 'wmoUnit:degC') {
        continue;
      }
      
      const windSpeedDict = properties['windSpeed'];
      const windSpeed = windSpeedDict['value'];
      if (windSpeed === null || windSpeedDict['unitCode'] != 'wmoUnit:km_h-1') {
        continue;
      }
      
      const humidityDict = properties['relativeHumidity'];
      const humidity = humidityDict['value'];
      if (humidityDict === null || humidityDict['unitCode'] != 'wmoUnit:percent') {
        continue;
      }
      
      if (hour in hourlyData) {
        const [hourlyTemp, hourlyWind, hourlyHumidity, hourlyCount, dayNum] = hourlyData[hour];
        hourlyData[hour] = [
          hourlyTemp + temperature,
          hourlyWind + windSpeed,
          hourlyHumidity + humidity,
          hourlyCount + 1,
          dayNum];
      } else {
        hourlyData[hour] = [temperature, windSpeed, humidity, 1, dayNum];
      }
        
      longitude = coordinates[0];
      latitude = coordinates[1];
      station = properties['station'].replace('https://api.weather.gov/stations/', '');
    }
    
    let weatherResults: WeatherResult[] = [];
    
    for (const hour of Object.keys(hourlyData)) {
      const [hourlyTemp, hourlyWind, hourlyHumidity, hourlyCount, dayNum] = hourlyData[hour];
      if (!(dayNum in numObs)) {
        numObs[dayNum] = 0;
        summedTemperature[dayNum] = 0;
        summedWindSpeed[dayNum] = 0;
        summedHumidity[dayNum] = 0;
      }
      numObs[dayNum] += 1;
      
      if (returnHourly) {
        weatherResults.push({
          inputLatitude: 0,
          inputLongitude: 0,
          hour: new Date(hour),
          temperature: hourlyTemp / hourlyCount,
          windSpeed: hourlyWind / hourlyCount,
          humidity: hourlyHumidity / hourlyCount,
          latitude: latitude,
          longitude: longitude,
          station: station
        });
      } else {
        summedTemperature[dayNum] += hourlyTemp / hourlyCount;
        summedWindSpeed[dayNum] += hourlyWind / hourlyCount;
        summedHumidity[dayNum] += hourlyHumidity / hourlyCount;
      }
    }
    
    if (!returnHourly) {
      for (const dayNumKey of Object.keys(numObs)) {
        const dayNum = Number(dayNumKey);
        console.log(dayNumKey, numObs[dayNumKey]);
        // check for 18 hours of data for each day (12 hours for first day)
        if ((dayNum < (numDays - 1) && numObs[dayNumKey] >= 15) ||
          (dayNum == (numDays - 1) && numObs[dayNumKey] >= 12)) {
          const obsDate = new Date(endDate);
          obsDate.setDate(obsDate.getDate() - dayNum);
          weatherResults.push({
            inputLatitude: 0,
            inputLongitude: 0,
            hour: obsDate,
            temperature: summedTemperature[dayNumKey] / numObs[dayNumKey],
            windSpeed: summedWindSpeed[dayNumKey] / numObs[dayNumKey],
            humidity: summedHumidity[dayNumKey] / numObs[dayNumKey],
            latitude: latitude,
            longitude: longitude,
            station: station
          });
        } else {
          weatherResults = [];
          break;
        }
      }
    }
    
    return weatherResults;
  }
  
  fetchHourlyData(locations: Location[], startDate: Date, endDate: Date): Observable<WeatherResult[]> {
    let stationRequests = [];
    for (const location of locations) {
      const url = this.buildStationUrl(location.latitude, location.longitude);
      stationRequests.push(this.http.get<{[key: string]: any}>(url));
    }
    forkJoin(stationRequests).subscribe(stationResults => {
      let obsRequests = [];
      for (const result of stationResults) {
        const station = this.extractStations(result, 1)[0];
        const url = this.buildObsUrl(station, startDate, endDate);
        obsRequests.push(this.http.get<{[key: string]: any}>(url));
      }
      forkJoin(obsRequests).subscribe(obsResults => {
        let fullResults = [];
        for (let i = 0; i < locations.length; i++) {
          for (const res of this.extractObservations(obsResults[i], true, endDate, 1)) {
            res.inputLatitude = locations[i].latitude;
            res.inputLongitude = locations[i].longitude;
            fullResults.push(res);
          }
        }
        this.subscriber.next(fullResults);
      });
    });
    
    return this.observable;
  }
}
